import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin,
  Clock,
  ArrowRight,
  Users,
  Shield,
  Zap
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { sendInvestmentInquiryEmail } from '../services/emailService';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    investmentAmount: '',
    preferredPlan: 'standard'
  });

  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    // Optionally, add validation here

    const result = await sendInvestmentInquiryEmail(formData);

    if (result.success) {
      setStatus({ type: 'success', message: 'Your inquiry has been sent! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        investmentAmount: '',
        preferredPlan: 'standard'
      });
    } else {
      setStatus({ type: 'error', message: 'There was an error sending your inquiry. Please try again later.' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Investment Hotline",
      details: ["+1 (657) 588-4977"],
      description: "24/7 Priority Support for Investors",
      color: "blue"
    },
    {
      icon: Mail,
      title: "Email Support",
      details: ["support@solentrawealth.com"],
      description: "Response within 2 hours for investment inquiries",
      color: "purple"
    },
    {
      icon: MapPin,
      title: "Investment Office",
      details: ["1301 Sansome st, San Francisco, CA 94111"],
      description: "Schedule a private consultation",
      color: "green"
    },
    {
      icon: Clock,
      title: "Trading Hours",
      details: ["24/7 Online Platform Access", "Office Hours: Mon-Fri 9AM-6PM EST"],
      description: "Investment processing available 24/7",
      color: "cyan"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Secure Communication",
      description: "All inquiries are encrypted and secure"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Get advice from certified investment professionals"
    },
    {
      icon: Zap,
      title: "Quick Response",
      description: "We respond to all inquiries within 2 hours"
    }
  ];

  const contactIconTone = {
    blue: 'border border-sky-400/25 bg-sky-400/10 text-sky-100',
    purple: 'border border-violet-400/25 bg-violet-400/10 text-violet-100',
    green: 'border border-emerald-400/25 bg-emerald-400/10 text-emerald-100',
    cyan: 'border border-cyan-400/25 bg-cyan-400/10 text-cyan-100'
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <Navigation />
      
      {/* Hero Section - Unique Design */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_0%,rgba(34,211,238,0.14),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(56,189,248,0.10),transparent_55%)]" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto py-16 md:py-20">
            <motion.div
              className="inline-flex items-center gap-2 mb-6 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-stone-200"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
              Portfolio advisory desk
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-semibold mb-6 leading-[1.05] tracking-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="text-white">Speak with an investment specialist</span>
              <span className="block text-cyan-200">about your portfolio mandate</span>
            </motion.h1>
            
            <motion.p 
              className="text-base md:text-xl mb-12 text-slate-300 leading-relaxed max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Whether you are evaluating a first allocation or scaling an existing program, we will help you translate goals into an actionable investment plan: risk budget, portfolio structure, execution workflow, and a reporting cadence your team can defend.
            </motion.p>

            {/* Features Grid */}
            <motion.div 
              className="grid md:grid-cols-3 gap-6 mb-12"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="card-glass p-6 rounded-3xl text-center">
                    <Icon className="mx-auto mb-4 text-cyan-300" size={30} />
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-400 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info Cards - Left Column */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight text-white">
                  Get in touch
                </h2>
                <p className="text-base md:text-lg text-slate-300 mb-8">
                  Our investment experts are ready to help you start your wealth-building journey. Choose your preferred method of contact.
                </p>
              </div>
                
                <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      className="card-glass p-6 rounded-3xl border border-white/10 card-hover"
                      whileHover={{ y: -2 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${contactIconTone[item.color] || contactIconTone.blue}`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                          {item.details.map((detail, idx) => (
                            <p key={idx} className="text-slate-300 text-sm mb-1">{detail}</p>
                          ))}
                          <p className="text-sm text-cyan-200 mt-2 font-medium">{item.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                </div>

              {/* Map Section */}
                <motion.div 
                className="card-glass rounded-3xl overflow-hidden border border-white/10 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
                  <iframe
                    title="Office Location"
                  className="w-full h-[300px]"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509379!2d-122.4194159247341!3d37.79531819093271!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085809c23c7986f%3A0xac23c5ad623e628!2s1301%20Sansome%20St%2C%20San%20Francisco%2C%20CA%2094111!5e0!3m2!1sen!2sus!4v1737171336000!5m2!1sen!2sus"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-allowed"
                    allowFullScreen
                  />
                </motion.div>
            </motion.div>
            
            {/* Contact Form Section - Right Column */}
            <motion.div 
              className="card-glass p-8 rounded-3xl"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Send Investment Inquiry</h3>
              
              {status.message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  status.type === 'success' 
                    ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                    : 'bg-red-500/20 border border-red-500/30 text-red-400'
                }`}>
                  {status.message}
            </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your full name"
                  />
                </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your email"
                  />
                </div>
              </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                      onChange={handleChange}
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Investment Amount</label>
                    <select
                      name="investmentAmount"
                      value={formData.investmentAmount}
                      onChange={handleChange}
                      className="input-glass w-full px-4 py-3 rounded-xl"
                    >
                      <option value="">Select amount range</option>
                      <option value="1k-5k">$1,000 - $5,000</option>
                      <option value="5k-10k">$5,000 - $10,000</option>
                      <option value="10k-25k">$10,000 - $25,000</option>
                      <option value="25k-50k">$25,000 - $50,000</option>
                      <option value="50k+">$50,000+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Preferred Investment Plan</label>
                  <select
                    name="preferredPlan"
                    value={formData.preferredPlan}
                    onChange={handleChange}
                    className="input-glass w-full px-4 py-3 rounded-xl"
                  >
                    <option value="Standard Plan">Standard Plan</option>
                    <option value="Premium Plan">Premium Plan</option>
                    <option value="Advanced Plan">Advanced Plan</option>
                    <option value="Platinum Plan">Platinum Plan</option>
                    <option value="Diamond Plan">Diamond Plan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="input-glass w-full px-4 py-3 rounded-xl"
                    placeholder="Investment inquiry subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                    required
                    rows={6}
                    className="input-glass w-full px-4 py-3 rounded-xl resize-none"
                    placeholder="Tell us about your investment goals and any questions you have..."
                />
              </div>

                <motion.button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors"
                      whileHover={{ y: -1 }}
                      whileTap={{ scale: 0.98 }}
                >
                  <span>Send Investment Inquiry</span>
                  <ArrowRight className="h-5 w-5" />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 border-t border-white/10 bg-stone-950">
        <div className="container mx-auto px-6 text-center">
          <div className="mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 p-10">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 tracking-tight text-white">Ready to start investing?</h2>
            <p className="text-base md:text-lg text-slate-300 mb-8">
            Join thousands of successful investors who have already started their wealth-building journey with Solentra Investments.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex w-full items-center justify-center rounded-xl bg-cyan-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 transition-colors sm:w-auto"
            >
              Start investing now
            </Link>
            <Link
              to="/investment-plans"
              className="inline-flex w-full items-center justify-center rounded-xl border border-white/15 bg-white/5 px-8 py-3 text-sm font-semibold text-stone-100 hover:bg-white/10 transition-colors sm:w-auto"
            >
              View investment plans
            </Link>
          </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Contact; 